// Field/category config for the Carer Essentials CRUD sections. Kept as
// plain data so the generic CrudSection component can render all four
// areas without four near-duplicate page implementations.

export const DOCUMENT_CATEGORIES = [
  "DBS", "Insurance", "Driving licence", "Right to work", "Training certificate",
  "Qualification", "Reference", "Contract/agreement", "Other",
];

export const WORK_TYPES = ["Live-in care", "Hourly care", "Night care", "Respite", "Companionship", "Other"];

export const SECTION_CONFIG = {
  documents: {
    apiSection: "documents",
    title: "My Documents",
    description: "DBS, insurance, licences, certificates and other professional documents — stored privately, never shown to families.",
    itemLabel: "document",
    titleField: "name",
    hasExpiry: true,
    allowFile: true,
    fields: [
      { name: "name", label: "Document name", type: "text", required: true },
      { name: "category", label: "Category", type: "select", options: DOCUMENT_CATEGORIES, defaultValue: "Other" },
      { name: "issue_date", label: "Issue date", type: "date" },
      { name: "expiry_date", label: "Expiry date", type: "date" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    listMeta: (item) => [item.category, item.expiry_date ? `Expires ${item.expiry_date}` : "No expiry date"],
  },
  cpd: {
    apiSection: "cpd",
    title: "Training & CPD",
    description: "A private record of training completed anywhere — not just through JCC.",
    itemLabel: "CPD entry",
    titleField: "title",
    hasExpiry: true,
    allowFile: true,
    fields: [
      { name: "title", label: "Course / activity title", type: "text", required: true },
      { name: "provider", label: "Provider", type: "text" },
      { name: "activity_type", label: "Activity type", type: "text", placeholder: "e.g. Online course, workshop, webinar" },
      { name: "completion_date", label: "Completion date", type: "date" },
      { name: "cpd_hours", label: "CPD hours", type: "number", step: "0.5" },
      { name: "expiry_date", label: "Renewal / expiry date", type: "date" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    listMeta: (item) => [item.provider, item.cpd_hours ? `${item.cpd_hours} CPD hours` : null, item.expiry_date ? `Renews ${item.expiry_date}` : null].filter(Boolean),
  },
  references: {
    apiSection: "references",
    title: "References",
    description: "A private organisational record of referees. JCC does not verify referees or references as part of this feature.",
    itemLabel: "reference",
    titleField: "referee_name",
    hasExpiry: false,
    allowFile: true,
    fields: [
      { name: "referee_name", label: "Referee name", type: "text", required: true },
      { name: "referee_role", label: "Referee role / relationship", type: "text" },
      { name: "organisation", label: "Organisation / company", type: "text" },
      { name: "email", label: "Email", type: "email" },
      { name: "phone", label: "Phone", type: "text" },
      { name: "placement_ref", label: "Placement / client identifier", type: "text" },
      { name: "dates_worked", label: "Dates worked", type: "text", placeholder: "e.g. Jan 2024 – Mar 2025" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    listMeta: (item) => [item.referee_role, item.organisation].filter(Boolean),
  },
  "work-records": {
    apiSection: "work-records",
    title: "Work Record",
    description: "A private record of your placements and work history — not a care-notes system.",
    itemLabel: "work record",
    titleField: "placement_ref",
    hasExpiry: false,
    allowFile: false,
    privacyNote: "Please avoid recording unnecessary confidential or sensitive client information — use initials or general identifiers where appropriate.",
    fields: [
      { name: "placement_ref", label: "Placement / client identifier", type: "text", required: true, placeholder: "e.g. Client A, Family S." },
      { name: "care_type", label: "Type of care", type: "text" },
      { name: "work_type", label: "Work type", type: "select", options: WORK_TYPES, defaultValue: "Other" },
      { name: "start_date", label: "Start date", type: "date" },
      { name: "end_date", label: "End date", type: "date" },
      { name: "duties", label: "Duties / experience", type: "textarea" },
      { name: "rate", label: "Rate (optional)", type: "text", placeholder: "e.g. £120/day" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    listMeta: (item) => [item.work_type, item.start_date ? `From ${item.start_date}` : null].filter(Boolean),
  },
};
